/**
 * Live processing state (data-map §9): while `results.aiProcessingStatus`
 * is pending/processing the page revalidates every 5s and polls
 * /resources/screening-job-status every 3s (1s initial delay) — v1's exact
 * cadence (v1:4713-4771). Polling uses REF-CALLBACK intervals (React 19
 * cleanup) instead of v1's useEffect, per the project rule; the section is
 * mounted only while polling should run, so unmount tears the timers down.
 * The overlay reuses ReportProgressOverlay with the ADDITIVE pre-screen
 * tips prop (v1's default carousel mentions credit/court records — wrong
 * for pre-screening).
 */
import { useCallback, useRef } from 'react'
import { useFetcher, useRevalidator } from 'react-router'
import {
	type ProgressStep,
	ReportProgressOverlay,
} from '#app/components/report-sections/ReportProgressOverlay'
import { ReportSkeleton } from '#app/components/report-sections/ReportSkeleton'
import type { ScreeningKind } from '#app/utils/application-detail-v2/types'

// No score tip — the pre-screening score is hidden on every surface (locked
// TT2-309 decision), so the generating overlay must not imply one is being
// computed. Full screening / family keep their score line (INDIVIDUAL_TIPS /
// FAMILY_TIPS in ReportProgressOverlay), since those surfaces do show a score.
const PRE_SCREEN_TIPS = [
	'Analyzing financial profile...',
	'Running AI risk assessment...',
	'Compiling verification results...',
]

interface JobStatusPayload {
	steps?: ProgressStep[]
	overall?: 'processing' | 'completed' | 'failed'
	error?: string
}

/**
 * Invisible marker owning one interval. The ref callback returns a cleanup
 * (React 19), so mounting starts the timer and unmounting clears it — the
 * sanctioned no-useEffect pattern. `onTick` is read through a ref so the
 * stable callback never re-attaches.
 */
function IntervalMarker({
	intervalMs,
	initialDelayMs,
	onTick,
}: {
	intervalMs: number
	initialDelayMs?: number
	onTick: () => void
}) {
	const latest = useRef(onTick)
	latest.current = onTick
	const attach = useCallback(
		(node: HTMLElement | null) => {
			if (!node) return
			const tick = () => latest.current()
			const delay =
				initialDelayMs != null ? setTimeout(tick, initialDelayMs) : null
			const interval = setInterval(tick, intervalMs)
			return () => {
				if (delay) clearTimeout(delay)
				clearInterval(interval)
			}
		},
		[intervalMs, initialDelayMs],
	)
	return <span hidden ref={attach} />
}

export function ProcessingSection({
	applicationId,
	applicantName,
	screeningKind,
	showOverlay,
}: {
	applicationId: string
	applicantName: string
	screeningKind: ScreeningKind
	/** False when the AI report already has predictions (v1 keeps polling
	 * but shows the report instead of the overlay, v1:7057-7072). */
	showOverlay: boolean
}) {
	const revalidator = useRevalidator()
	const jobFetcher = useFetcher<JobStatusPayload>()

	const pollJob = () => {
		try {
			void jobFetcher.load(
				`/resources/screening-job-status?applicationId=${applicationId}`,
			)
		} catch {
			// Route manifest may not be ready on first call; retry next tick.
		}
	}

	const steps = Array.isArray(jobFetcher.data?.steps)
		? jobFetcher.data.steps
		: null
	const overall = jobFetcher.data?.overall ?? 'processing'

	return (
		<>
			<IntervalMarker
				intervalMs={5000}
				onTick={() => void revalidator.revalidate()}
			/>
			<IntervalMarker
				intervalMs={3000}
				initialDelayMs={1000}
				onTick={pollJob}
			/>
			{showOverlay ? (
				steps ? (
					<ReportProgressOverlay
						variant="individual"
						steps={steps}
						overall={overall}
						applicantName={applicantName}
						tips={
							screeningKind === 'pre_screening' ? PRE_SCREEN_TIPS : undefined
						}
					/>
				) : (
					<ReportSkeleton />
				)
			) : null}
		</>
	)
}
